import { create } from 'zustand';
import { nanoid } from 'nanoid';

export const useProjectStore = create((set, get) => ({
  // 状態
  projects: [],
  currentProject: null,
  nodes: [],
  edges: [],
  
  // プロジェクト一覧読み込み
  loadProjects: () => {
    try {
      const saved = localStorage.getItem('ai-studio-projects');
      const projects = saved ? JSON.parse(saved) : [];
      set({ projects });
      
      // 最後のプロジェクトを読み込み
      const lastProjectId = localStorage.getItem('ai-studio-last-project');
      if (lastProjectId) {
        const lastProject = projects.find(p => p.id === lastProjectId);
        if (lastProject) {
          get().loadProject(lastProjectId);
        }
      }
    } catch (error) {
      console.error('プロジェクト読み込みエラー:', error);
    }
  },
  
  // プロジェクト保存
  saveProjects: (projects) => {
    try {
      localStorage.setItem('ai-studio-projects', JSON.stringify(projects));
      set({ projects });
    } catch (error) {
      console.error('プロジェクト保存エラー:', error);
    }
  },
  
  // 新規プロジェクト作成
  createProject: (projectData) => {
    const newProject = {
      id: nanoid(),
      name: projectData.name,
      description: projectData.description || '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      nodes: [],
      edges: [],
      executions: {}
    };
    
    const { projects, saveProjects } = get();
    const updatedProjects = [...projects, newProject];
    saveProjects(updatedProjects);
    
    return newProject;
  },
  
  // プロジェクト読み込み
  loadProject: (projectId) => {
    const { projects } = get();
    const project = projects.find(p => p.id === projectId);
    
    if (project) {
      set({
        currentProject: project,
        nodes: project.nodes || [],
        edges: project.edges || []
      });
      
      // 最後のプロジェクトとして記録
      localStorage.setItem('ai-studio-last-project', projectId);
    }
  },
  
  // プロジェクト保存
  saveProject: () => {
    const { currentProject, nodes, edges, projects, saveProjects } = get();
    
    if (!currentProject) return;
    
    const updatedProject = {
      ...currentProject,
      nodes,
      edges,
      updatedAt: new Date().toISOString()
    };
    
    const updatedProjects = projects.map(p => 
      p.id === currentProject.id ? updatedProject : p
    );
    
    saveProjects(updatedProjects);
    set({ currentProject: updatedProject });
  },
  
  // プロジェクト削除
  deleteProject: (projectId) => {
    const { projects, currentProject, saveProjects } = get();
    const updatedProjects = projects.filter(p => p.id !== projectId);
    saveProjects(updatedProjects);
    
    // 現在のプロジェクトが削除された場合
    if (currentProject && currentProject.id === projectId) {
      set({
        currentProject: null,
        nodes: [],
        edges: []
      });
      localStorage.removeItem('ai-studio-last-project');
    }
  },
  
  // ノード追加
  addNode: (nodeData) => {
    const newNode = {
      id: nanoid(),
      type: 'aiNode',
      position: nodeData.position || { x: 100, y: 100 },
      data: {
        nodeType: nodeData.nodeType,
        label: nodeData.label,
        inputs: nodeData.inputs || {},
        outputs: nodeData.outputs || {},
        config: nodeData.config || {}
      }
    };
    
    const { nodes } = get();
    const updatedNodes = [...nodes, newNode];
    set({ nodes: updatedNodes });
    get().saveProject();
    
    return newNode;
  },
  
  // ノード更新
  updateNode: (nodeId, updates) => {
    const { nodes } = get();
    const updatedNodes = nodes.map(node =>
      node.id === nodeId
        ? { ...node, data: { ...node.data, ...updates } }
        : node
    );
    set({ nodes: updatedNodes });
    get().saveProject();
  },
  
  // ノード削除
  deleteNode: (nodeId) => {
    const { nodes, edges } = get();
    const updatedNodes = nodes.filter(node => node.id !== nodeId);
    const updatedEdges = edges.filter(edge => 
      edge.source !== nodeId && edge.target !== nodeId
    );
    
    set({ 
      nodes: updatedNodes,
      edges: updatedEdges
    });
    get().saveProject();
  },
  
  // エッジ追加
  addEdge: (edgeData) => {
    const newEdge = {
      id: nanoid(),
      ...edgeData
    };
    
    const { edges } = get();
    const updatedEdges = [...edges, newEdge];
    set({ edges: updatedEdges });
    get().saveProject();
    
    return newEdge;
  },
  
  // エッジ削除
  deleteEdge: (edgeId) => {
    const { edges } = get();
    const updatedEdges = edges.filter(edge => edge.id !== edgeId);
    set({ edges: updatedEdges });
    get().saveProject();
  },
  
  // ノード位置更新
  updateNodePosition: (nodeId, position) => {
    const { nodes } = get();
    const updatedNodes = nodes.map(node =>
      node.id === nodeId
        ? { ...node, position }
        : node
    );
    set({ nodes: updatedNodes });
    get().saveProject();
  },
  
  // 実行情報を保存
  saveExecution: (executionId, executionData) => {
    const { currentProject, projects, saveProjects } = get();
    
    if (!currentProject) return;
    
    const updatedProject = {
      ...currentProject,
      executions: {
        ...currentProject.executions,
        [executionId]: executionData
      }
    };
    
    const updatedProjects = projects.map(p => 
      p.id === currentProject.id ? updatedProject : p
    );
    
    saveProjects(updatedProjects);
    set({ currentProject: updatedProject });
  },
  
  // ワークフロー実行
  executeWorkflow: async (startNodeId) => {
    const { nodes, edges, currentProject } = get();
    
    if (!currentProject) {
      throw new Error('プロジェクトが選択されていません');
    }
    
    // 実行順序の計算
    const executionOrder = calculateExecutionOrder(nodes, edges, startNodeId);
    
    if (executionOrder.length === 0) {
      throw new Error('実行できるノードがありません');
    }
    
    // 実行ID生成
    const executionId = `exec_${Date.now()}_${nanoid(6)}`;
    
    // 実行情報保存
    const executionData = {
      id: executionId,
      startNodeId,
      executionOrder,
      status: 'started',
      startTime: new Date().toISOString(),
      results: {}
    };
    
    get().saveExecution(executionId, executionData);
    
    return executionId;
  }
}));

// 実行順序計算（トポロジカルソート）
const calculateExecutionOrder = (nodes, edges, startNodeId) => {
  const visited = new Set();
  const order = [];
  
  const visit = (nodeId) => {
    if (visited.has(nodeId)) return;
    visited.add(nodeId);
    
    // 依存ノードを先に処理
    const dependencies = edges
      .filter(edge => edge.target === nodeId)
      .map(edge => edge.source);
    
    dependencies.forEach(visit);
    
    // このノードを追加
    const node = nodes.find(n => n.id === nodeId);
    if (node) {
      order.push(nodeId);
    }
  };
  
  if (startNodeId) {
    visit(startNodeId);
  } else {
    // 入力エッジがないノードから開始
    const startNodes = nodes.filter(node => 
      !edges.some(edge => edge.target === node.id)
    );
    startNodes.forEach(node => visit(node.id));
  }
  
  return order;
};